/**
 * Titanic Syndrome into a Pressure added before it existed — a one-time upgrade
 * applied to the workspace as it loads (owner, 2026-10-14).
 *
 * A Pressure's rows are copied from the catalog when it is added, so one added
 * before 2026-10-14 has no Titanic row, and its Hindenburg sits at level 10 —
 * the height Titanic now takes (Hindenburg moved to 11 to make room).
 *
 * **The trigger is that signature: a Hindenburg at level 10.** Nothing else
 * carries it, so the upgrade runs once — afterwards Hindenburg is at 11 — and a
 * Titanic row the user removes later is never put back. A pane whose Hindenburg
 * was deleted is left alone.
 *
 * Titanic is cloned from that Hindenburg, so it keeps the pane, axis, list and
 * any marker size the user set; only the alias, level, shape and colour are
 * Titanic's own (`PRESSURE_LOOK` in `services/qv-data/app/qf/breadth.py`).
 *
 * **The colours of the spec before 2026-10-03's correction** are fixed here too
 * (owner, 2026-10-14): Bango positive, Bingo negative and Defib negative, all
 * three in one pane, is that spec's signature — the catalog has Bango negative,
 * Bingo positive, Defib positive. Matched as a set so it runs once; a single
 * colour the user changed afterwards is theirs and is left alone.
 *
 * Pure, with `mintId` injected, like `add-indicator.ts`. Unchanged subtrees keep
 * their identity, so a workspace with nothing to upgrade comes back as itself.
 */
import type { ChartGroup, Pane, Plot, UserWorkspace } from "@qufolio/model";

const HINDENBURG = "jasHindenburg";
const TITANIC = "jasTitanic";
const OLD_LEVEL = 10;
const HINDENBURG_LEVEL = 11;
const TITANIC_LEVEL = 10;

function isOldHindenburg(plot: Plot): boolean {
  return head(plot) === HINDENBURG && plot.type === "signal" && plot.signal?.level === OLD_LEVEL;
}

/** The pre-correction colours, by output, and what the catalog says now. */
const OLD_COLOURS: Record<string, [old: string, now: string]> = {
  jasBongoUp: ["positive", "negative"],
  jasBongoDown: ["negative", "positive"],
  jasDefib: ["negative", "positive"],
};

function head(plot: Plot): string | undefined {
  return plot.outputAlias?.split("_", 1)[0];
}

function colourOf(plot: Plot): string | undefined {
  return plot.palette?.upColor ?? plot.style?.stroke;
}

function hasOldColours(plots: readonly Plot[]): boolean {
  return Object.entries(OLD_COLOURS).every(([name, [old]]) =>
    plots.some((p) => head(p) === name && colourOf(p) === old),
  );
}

function recolour(plots: readonly Plot[]): Plot[] {
  return plots.map((plot) => {
    const fix = OLD_COLOURS[head(plot) ?? ""];
    if (!fix || colourOf(plot) !== fix[0]) return plot;
    return {
      ...plot,
      style: { ...plot.style, stroke: fix[1] },
      palette: { ...plot.palette, upColor: fix[1] },
    };
  });
}

function upgradePane(pane: Pane, mintId: () => string): Pane {
  const original = pane.plots;
  if (!original) return pane;
  const recoloured = hasOldColours(original);
  const plots = recoloured ? recolour(original) : original;
  if (!plots.some(isOldHindenburg)) return recoloured ? { ...pane, plots } : pane;
  const out: Plot[] = [];
  for (const plot of plots) {
    if (!isOldHindenburg(plot)) {
      out.push(plot);
      continue;
    }
    out.push({ ...plot, signal: { ...plot.signal!, level: HINDENBURG_LEVEL } });
    out.push({
      ...plot,
      id: mintId(),
      outputAlias: plot.outputAlias!.replace(HINDENBURG, TITANIC),
      signal: { ...plot.signal!, level: TITANIC_LEVEL },
      style: { ...plot.style, stroke: "negative", markerShape: "arrowDown" },
      palette: { ...plot.palette, upColor: "negative" },
    });
  }
  return { ...pane, plots: out };
}

function upgradeGroup(group: ChartGroup, mintId: () => string): ChartGroup {
  let changed = false;
  const panes = group.panes.map((pane) => {
    const next = upgradePane(pane, mintId);
    if (next !== pane) changed = true;
    return next;
  });
  return changed ? { ...group, panes } : group;
}

/** The workspace with every old Pressure given its Titanic row; itself if none. */
export function upgradePressure(workspace: UserWorkspace, mintId: () => string): UserWorkspace {
  let changed = false;
  const layouts = workspace.layouts.map((layout) => {
    let layoutChanged = false;
    const sections = layout.sections.map((section) => {
      if (section.kind !== "chartGroup") return section;
      const group = section.config as ChartGroup;
      if (!Array.isArray(group?.panes)) return section;
      const next = upgradeGroup(group, mintId);
      if (next === group) return section;
      layoutChanged = true;
      return { ...section, config: next };
    });
    if (!layoutChanged) return layout;
    changed = true;
    return { ...layout, sections };
  });
  return changed ? { ...workspace, layouts } : workspace;
}
